/**
 * Cadastro e edição de máquina em etapas (rotas /machines/new e /machines/:id/edit) — issue #362.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { PageShell } from '../../components';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useAcao } from '../../authorization/useVeredito';
import { departmentService } from '../../services/departmentService';
import { machineService, Machine } from '../../services/machineService';
import { sourceGeneratorService, SourceGenerator } from '../../services/sourceGeneratorService';
import type { Department } from '../../types/department';
import { MachineWizard } from './components/MachineWizard';
import { EMPTY_IDENTIFICATION, IdentificationValues } from './components/MachineWizard/MachineWizard.types';
import { errorMessage } from './components/MachineWizard/machineWizardValidation';

/** Os setores do cadastro; só são pedidos a quem pode ler setores (`useListaPermitida`). */
const loadDepartments = async (companyId: string): Promise<readonly Department[]> =>
  (await departmentService.getDepartments({ limit: 200, ...(companyId === '' ? {} : { companyId }) })).data;

const valuesFrom = (machine: Machine, sourceGenerator: SourceGenerator | null): IdentificationValues => ({
  companyId: '',
  name: machine.name,
  tag: machine.tag ?? '',
  departmentId: machine.departmentId === null ? '' : String(machine.departmentId),
  sourceGenerator,
  manufacturer: machine.manufacturer ?? '',
  model: machine.model ?? '',
  serialNumber: machine.serialNumber ?? '',
  yearOfManufacture: machine.yearOfManufacture === null ? '' : String(machine.yearOfManufacture),
  location: machine.location ?? '',
  status: machine.status,
  observations: machine.observations ?? '',
  maintenanceFrequency: machine.maintenanceFrequency ?? '',
  lastMaintenanceDate: machine.lastMaintenanceDate?.split('T')[0] ?? '',
  nextMaintenanceDate: machine.nextMaintenanceDate?.split('T')[0] ?? '',
});

const MachineWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const create = useAcao('machine.create', 'Cadastrar máquina');
  const update = useAcao('machine.update', 'Editar máquina');
  const canEditItems = !update.disabled;
  const readGenerators = useAcao('source_generator.read', 'Tipos de equipamento');
  const canReadGenerators = !readGenerators.disabled;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<IdentificationValues>(EMPTY_IDENTIFICATION);
  const [machineName, setMachineName] = useState<string | null>(null);

  const allowed = id ? !update.disabled : !create.disabled;
  // Com empresa escolhida na etapa 1, só os setores dela são buscados; sem escolha, vale a lista toda.
  const [companyId, setCompanyId] = useState('');
  const loadCompanyDepartments = useCallback(() => loadDepartments(companyId), [companyId]);
  const departmentList = useListaPermitida('department.read', 'Setores', loadCompanyDepartments, {
    habilitado: allowed,
  });
  const departments = useMemo(() => [...departmentList.itens], [departmentList.itens]);

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return undefined;
    }
    let active = true;

    const load = async () => {
      try {
        const machine = id ? await machineService.getMachineById(id) : null;
        if (!active) return;

        if (machine) {
          const generator = machine.sourceGeneratorId && canReadGenerators
            ? await sourceGeneratorService.getSourceGeneratorById(machine.sourceGeneratorId).catch(() => null)
            : null;
          if (!active) return;
          setInitialValues(valuesFrom(machine, generator));
          setMachineName(machine.name);
        }
      } catch (err: unknown) {
        if (active) setError(errorMessage(err, 'Não foi possível abrir a máquina.'));
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [id, allowed, canReadGenerators]);

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Máquinas', path: '/machines' },
    { label: id ? (machineName ?? 'Editar máquina') : 'Nova máquina' },
  ];
  const title = id ? 'Editar máquina' : 'Cadastrar máquina';

  if (!allowed) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Alert
          severity="info"
          action={
            <Button color="inherit" onClick={() => navigate('/machines')} sx={{ minHeight: 44 }}>
              Voltar para as máquinas
            </Button>
          }
        >
          Seu acesso permite ver as máquinas, mas não {id ? 'alterá-las' : 'cadastrar novas'}. Peça a quem
          administra o seu acesso.
        </Alert>
      </PageShell>
    );
  }

  return (
    <PageShell breadcrumbs={breadcrumbs} title={title} subtitle="Cadastro em etapas: você pode parar e continuar depois.">
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Carregando" />
        </Box>
      )}
      {!loading && error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && (
        <MachineWizard
          machineId={id}
          initialValues={initialValues}
          departments={departments}
          canEditItems={canEditItems}
          onCompanyChange={setCompanyId}
          onFinish={() => navigate('/machines')}
          onCancel={() => navigate('/machines')}
        />
      )}
    </PageShell>
  );
};

export default MachineWizardPage;
